/**
 * GDSModalManager.js
 * Orchestrates modal operations and delegates to specific modal handlers
 */
import { getSizedShopifyUrl, formatMoney } from './GDSModules.js';

import { GDSEditModal } from './GDSEditModal.js';
import { GDSPreviewModal } from './GDSPreviewModal.js';
import { GDSEnvironmentModal } from './GDSEnvironmentModal.js';
import { GDSScrollLockManager } from './GDSScrollLockManager.js';

export class GDSModalManager {
    constructor(builder) {
        this.builder = builder;
        this.cardManager = builder.cardManager;

        // Initialize specific modal handlers
        this.editModal = null;
        this.previewModal = new GDSPreviewModal(this);
        this.environmentModal = new GDSEnvironmentModal(this);

        // Track open modals
        this.activeModals = new Set();
    }

    // ========================================================================
    // COMMON MODAL OPERATIONS
    // ========================================================================

    getEditModal() {
        if (!this.editModal) {
            // Check if cardManager exists before creating editModal
            if (!this.builder.cardManager) {
                console.error('Cannot create GDSEditModal: cardManager not initialized');
                return null;
            }
            this.editModal = new GDSEditModal(this);
        }
        return this.editModal;
    }

    /**
     * Opens any modal with common setup
     */
    openModal(modal) {
        if (!modal) return;

        GDSScrollLockManager.lock();

        modal.classList.remove('hidden');
        this.activeModals.add(modal);
    }

    /**
     * Closes any modal with common cleanup
     */
    closeModal(modal) {
        if (!modal) return;

        modal.classList.add('hidden');
        this.activeModals.delete(modal);

        // Restore body scroll if no modals are open
        GDSScrollLockManager.unlock();
    }

    /**
     * Setup scroll prevention for any modal
     */
    setupModalScrollPrevention(modal) {
        // Prevent wheel events on the modal backdrop from scrolling the page
        modal.addEventListener('wheel', (e) => {
            if (e.target === modal ||
                e.target.classList.contains('modal-backdrop')) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });

        // Prevent touch events on backdrop from scrolling the page (mobile)
        modal.addEventListener('touchmove', (e) => {
            if (e.target === modal ||
                e.target.classList.contains('modal-backdrop')) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });

        // Allow scrolling within the modal content areas
        const scrollableAreas = modal.querySelectorAll(
            '.overflow-y-auto, .modal-options-container, .modal-content'
        );
        scrollableAreas.forEach(area => {
            area.style.overscrollBehavior = 'contain';
        });
    }

    // ========================================================================
    // MODAL TYPE DELEGATION
    // ========================================================================

    /**
     * Opens the edit/customize modal for a product card
     */
    openEditModal(modal) {
        const editModal = this.getEditModal();
        if (editModal) {
            editModal.open(modal);
        }
    }

    /**
     * Opens the product card modal (legacy method for compatibility)
     */
    openCardModal(modal) {
        this.openEditModal(modal);
    }

    /**
     * Closes the edit/customize modal
     */
    closeEditModal(modal) {
        const editModal = this.getEditModal();
        if (editModal) {
            editModal.close(modal);
        }
    }

    /**
     * Closes the product card modal (legacy method for compatibility)
     */
    closeCardModal(modal) {
        if (!modal) return;

        // Check if this is an environment modal
        if (modal.dataset.modalType === 'environment') {
            // If so, call the environment modal's specific close method
            this.environmentModal.close(modal);
        } else {
            // Otherwise, assume it's an edit modal and use the original flow
            this.closeEditModal(modal);
        }
    }

    /**
     * Opens the product preview modal
     */
    async openPreviewModal(productHandle) {
        await this.previewModal.open(productHandle);
    }

    /**
     * Opens an environment info modal
     */
    openEnvironmentModal(modalId) {
        this.environmentModal.open(modalId);
    }

    /**
     * Creates the HTML for edit modals
     * FIX: Use getEditModal() to ensure editModal is initialized
     */
    createProductModalHTML(product, cardTitle, cardData, modalId, showEditButton = true) {
        const editModal = this.getEditModal();
        if (!editModal) {
            console.error('Could not create edit modal for product modal HTML');
            return '';
        }

        // Check if editModal has createHTML method
        if (typeof editModal.createHTML !== 'function') {
            console.error('Edit modal does not have createHTML method');
            return '';
        }

        return editModal.createHTML(product, cardTitle, cardData, modalId, showEditButton);
    }

    // ========================================================================
    // EDIT MODAL OPERATIONS (delegated to EditModal but exposed for EventHandler)
    // ========================================================================

    /**
     * Handles option selection in edit modal
     */
    handleOptionSelection(modal, card, field, value) {
        const editModal = this.getEditModal();
        if (editModal) {
            editModal.handleOptionSelection(modal, card, field, value);
        }
    }

    /**
     * Handles save action in edit modal
     */
    async handleModalSave(modal, card) {
        const editModal = this.getEditModal();
        if (editModal) {
            await editModal.handleSave(modal, card);
        }
    }

    /**
     * Populates modal options (called by EditModal)
     */
    populateModalOptions(modal, card) {
        const editModal = this.getEditModal();
        if (editModal) {
            editModal.populateOptions(modal, card);
        }
    }

    // ========================================================================
    // UTILITY METHODS
    // ========================================================================

    /**
     * Checks if any modal is currently open
     */
    hasOpenModals() {
        return this.activeModals.size > 0;
    }

    /**
     * Closes all open modals
     */
    closeAllModals() {
        this.activeModals.forEach(modal => this.closeModal(modal));
    }

    /**
     * Gets a reference to the builder
     */
    getBuilder() {
        return this.builder;
    }

    /**
     * Gets a reference to the card manager
     */
    getCardManager() {
        return this.builder.cardManager;
    }

    /**
     * Clean up method for removing event listeners
     */
    removeGlobalListeners() {
        // Add any global listener cleanup here if needed
    }
}
